import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertCircle, Edit, Eye, Plus, Search, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';

interface ServiceProvider {
  id: string;
  name: string;
  isActive: boolean;
}

interface ServiceDefinition {
  id: string;
  providerId: string | null;
  serviceIdentifier: string;
  displayName: string;
  serviceType: string;
  intelligenceTier: string | null;
  usageBased: boolean;
  creditsPerAction: number | null;
  inputCostUsdPerMillion: number | null;
  outputCostUsdPerMillion: number | null;
  costUsdPerUnit: number | null;
  costUnit: string | null;
  isRecurring: boolean;
  billingPeriod: string | null;
  billedToUser: boolean;
  actionQualifier: string;
  maxTokens: number | null;
  supportsStreaming: boolean;
  supportsFunctionCalling: boolean;
  supportsVision: boolean;
  isActive: boolean;
  deprecationDate: string | null;
  replacementServiceId: string | null;
  metadata: Record<string, any> | null;
  provider?: ServiceProvider;
  createdAt: string;
  updatedAt: string;
}

interface EditableService extends ServiceDefinition {
  isEditing?: boolean;
}

// API endpoints are now handled through the api instance from @/lib/api
const API_PATH = '/admin/service-definitions';

const ServiceDefinitionsPage: React.FC = () => {
  const [services, setServices] = useState<EditableService[]>([]);
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedService, setSelectedService] = useState<ServiceDefinition | null>(null);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editDialogService, setEditDialogService] = useState<Partial<ServiceDefinition>>({});
  const [filterType, setFilterType] = useState<string>('all');
  const [filterProvider, setFilterProvider] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  // const [editingService, setEditingService] = useState<Partial<ServiceDefinition>>({});
  const [newService, setNewService] = useState<Partial<ServiceDefinition>>({
    serviceType: 'llm',
    actionQualifier: 'single',
    usageBased: false,
    isRecurring: false,
    billedToUser: true,
    supportsStreaming: false,
    supportsFunctionCalling: false,
    supportsVision: false,
    isActive: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [servicesResponse, providersResponse] = await Promise.all([
        api.get(`${API_PATH}?limit=100`),
        api.get(`${API_PATH}/providers`)
      ]);

      const servicesData = servicesResponse.data;
      const providersData = providersResponse.data;

      if (!servicesData.success) {
        throw new Error(servicesData.error || 'Failed to fetch service definitions');
      }

      if (!providersData.success) {
        throw new Error(providersData.error || 'Failed to fetch service providers');
      }

      setServices(servicesData.data.services.map((s: ServiceDefinition) => ({ ...s, isEditing: false })));
      setProviders(providersData.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditStart = (service: ServiceDefinition) => {
    setEditDialogService({ ...service });
    setIsEditDialogOpen(true);
  };

  const handleEditCancel = () => {
    setIsEditDialogOpen(false);
    setEditDialogService({});
  };

  const handleEditSave = async () => {
    const serviceId = editDialogService.id;
    if (!serviceId) return;
    try {
      setSaving(true);
      
      // Only send editable fields to the backend
      const updateData = {
        providerId: editDialogService.providerId,
        serviceIdentifier: editDialogService.serviceIdentifier,
        displayName: editDialogService.displayName,
        serviceType: editDialogService.serviceType,
        intelligenceTier: editDialogService.intelligenceTier,
        usageBased: editDialogService.usageBased,
        creditsPerAction: editDialogService.creditsPerAction,
        inputCostUsdPerMillion: editDialogService.inputCostUsdPerMillion,
        outputCostUsdPerMillion: editDialogService.outputCostUsdPerMillion,
        costUsdPerUnit: editDialogService.costUsdPerUnit,
        costUnit: editDialogService.costUnit,
        isRecurring: editDialogService.isRecurring,
        billingPeriod: editDialogService.billingPeriod,
        billedToUser: editDialogService.billedToUser,
        actionQualifier: editDialogService.actionQualifier,
        maxTokens: editDialogService.maxTokens,
        supportsStreaming: editDialogService.supportsStreaming,
        supportsFunctionCalling: editDialogService.supportsFunctionCalling,
        supportsVision: editDialogService.supportsVision,
        isActive: editDialogService.isActive,
        deprecationDate: editDialogService.deprecationDate,
        replacementServiceId: editDialogService.replacementServiceId,
        metadata: editDialogService.metadata
      };
      
      // Remove undefined values
      Object.keys(updateData).forEach(key => {
        if (updateData[key as keyof typeof updateData] === undefined) {
          delete updateData[key as keyof typeof updateData];
        }
      });
      
      const response = await api.put(`${API_PATH}/${serviceId}`, updateData);
      const result = response.data;

      if (!result.success) {
        throw new Error(result.error || 'Failed to update service');
      }

      // Update the service in the list
      setServices(prev => prev.map(s => 
        s.id === serviceId ? { ...result.data, isEditing: false } : s
      ));
      setIsEditDialogOpen(false);
      setEditDialogService({});
    } catch (err) {
      console.error('Failed to save service:', err);
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleServiceClick = (service: ServiceDefinition) => {
    setSelectedService(service);
    setIsDetailsDialogOpen(true);
  };

  const handleAddService = async () => {
    // Validate required fields
    if (!newService.serviceIdentifier || !newService.displayName || !newService.serviceType) {
      setError('Please fill in all required fields (Service Identifier, Display Name, and Service Type)');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Clean the data - remove null/undefined values for optional fields
      const cleanedService = Object.entries(newService).reduce((acc, [key, value]) => {
        // Skip null, undefined, and empty string values for optional numeric fields
        if (value === null || value === undefined || value === '') {
          return acc;
        }
        acc[key] = value;
        return acc;
      }, {} as any);

      console.log('Sending service data:', cleanedService);
      const response = await api.post(API_PATH, cleanedService);
      const data = response.data;

      if (!data.success) {
        throw new Error(data.error || 'Failed to create service definition');
      }

      // Add the new service to the list
      setServices([data.data, ...services]);
      
      // Reset the form
      setNewService({
        serviceType: 'llm',
        actionQualifier: 'single',
        usageBased: false,
        isRecurring: false,
        billedToUser: true,
        supportsStreaming: false,
        supportsFunctionCalling: false,
        supportsVision: false,
        isActive: true
      });
      
      // Close the dialog
      setIsAddDialogOpen(false);
    } catch (err: any) {
      console.error('Failed to add service:', err);
      console.error('Error response data:', err.response?.data);
      console.error('Error response status:', err.response?.status);
      
      // Try to extract the actual error message from the response
      let errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to add service';
      
      // If there are validation details, format them nicely
      if (err.response?.data?.details && Array.isArray(err.response.data.details)) {
        const details = err.response.data.details.map((detail: any) => {
          const field = detail.path?.join('.') || 'unknown field';
          return `${field}: ${detail.message}`;
        }).join(', ');
        errorMessage = `${errorMessage} - ${details}`;
      }
      
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const getServiceTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'llm': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'image_generation': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'embedding': return 'bg-green-100 text-green-800 border-green-200';
      case 'storage': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'search': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'knowledge_base': return 'bg-teal-100 text-teal-800 border-teal-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const filteredServices = services.filter((service) => {
    const matchesType = filterType === 'all' || service.serviceType === filterType;
    const matchesProvider = filterProvider === 'all' || 
                           (service.provider && service.provider.id === filterProvider);
    const matchesSearch = searchQuery === '' || 
                         service.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         service.serviceIdentifier.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesType && matchesProvider && matchesSearch;
  });

  const uniqueServiceTypes = [...new Set(services.map(s => s.serviceType))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Service Definitions</h1>
          <p className="text-muted-foreground">
            Manage LLM models, pricing, and service configurations
          </p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Service
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex space-x-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search services..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {uniqueServiceTypes.map((type) => (
              <SelectItem key={type} value={type}>
                {type.toUpperCase()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterProvider} onValueChange={setFilterProvider}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by provider" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Providers</SelectItem>
            {providers.map((provider) => (
              <SelectItem key={provider.id} value={provider.id}>
                {provider.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Service Definitions</CardTitle>
          <CardDescription>
            {filteredServices.length} service{filteredServices.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Service Name</TableHead>
                  <TableHead>Identifier</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Pricing</TableHead>
                  <TableHead>Max Tokens</TableHead>
                  <TableHead>Capabilities</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[120px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredServices.map((service) => (
                  <TableRow key={service.id} className={service.isEditing ? 'bg-muted/50' : ''}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{service.displayName}</div>
                        <div className="text-xs text-muted-foreground">
                          {service.intelligenceTier && `${service.intelligenceTier} tier`}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-1 py-0.5 rounded">
                        {service.serviceIdentifier}
                      </code>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{service.provider?.name || 'Unknown'}</span>
                    </TableCell>
                    <TableCell>
                      <Badge className={getServiceTypeColor(service.serviceType)}>
                        {service.serviceType.toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <span className="text-sm">
                          {service.usageBased ? 'Usage-based' : (service.creditsPerAction || 'N/A')}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {service.maxTokens ? service.maxTokens.toLocaleString() : 'N/A'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {service.supportsStreaming && (
                          <Badge variant="outline" className="text-xs">Stream</Badge>
                        )}
                        {service.supportsFunctionCalling && (
                          <Badge variant="outline" className="text-xs">Functions</Badge>
                        )}
                        {service.supportsVision && (
                          <Badge variant="outline" className="text-xs">Vision</Badge>
                        )}
                        {!service.supportsStreaming && !service.supportsFunctionCalling && !service.supportsVision && (
                          <span className="text-xs text-muted-foreground">None</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={service.isActive ? 'default' : 'secondary'}>
                        {service.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleServiceClick(service)}
                        >
                          <Eye className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditStart(service)}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {filteredServices.length === 0 && !loading && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No services found</h3>
            <p className="text-muted-foreground text-center">
              {services.length === 0
                ? "Get started by adding your first service definition."
                : "No services match your current filters. Try adjusting your search criteria."
              }
            </p>
            {services.length === 0 && (
              <Button className="mt-4" onClick={() => setIsAddDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Service
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Service Details Dialog */}
      <Dialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedService?.displayName}</DialogTitle>
            <DialogDescription>
              Service definition details and configuration
            </DialogDescription>
          </DialogHeader>
          
          {selectedService && (
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Basic Information
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Service Identifier</label>
                    <p className="text-sm text-muted-foreground">{selectedService.serviceIdentifier}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Provider</label>
                    <p className="text-sm text-muted-foreground">
                      {selectedService.provider?.name || 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Service Type</label>
                    <Badge className={getServiceTypeColor(selectedService.serviceType)}>
                      {selectedService.serviceType.toUpperCase()}
                    </Badge>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Intelligence Tier</label>
                    <p className="text-sm text-muted-foreground">
                      {selectedService.intelligenceTier || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Pricing Configuration */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Pricing Configuration
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Pricing Model</label>
                    <p className="text-sm text-muted-foreground">
                      {selectedService.usageBased ? 'Usage-based' : 'Fixed credits'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Billed to User</label>
                    <p className="text-sm text-muted-foreground">
                      {selectedService.billedToUser ? 'Yes' : 'No'}
                    </p>
                  </div>

                  {selectedService.usageBased ? (
                    <>
                      {selectedService.inputCostUsdPerMillion && (
                        <div>
                          <label className="text-sm font-medium">Input Cost</label>
                          <p className="text-sm text-muted-foreground">
                            ${selectedService.inputCostUsdPerMillion} per 1M tokens
                          </p>
                        </div>
                      )}
                      {selectedService.outputCostUsdPerMillion && (
                        <div>
                          <label className="text-sm font-medium">Output Cost</label>
                          <p className="text-sm text-muted-foreground">
                            ${selectedService.outputCostUsdPerMillion} per 1M tokens
                          </p>
                        </div>
                      )}
                      {selectedService.costUsdPerUnit && (
                        <div>
                          <label className="text-sm font-medium">Unit Cost</label>
                          <p className="text-sm text-muted-foreground">
                            ${selectedService.costUsdPerUnit} per {selectedService.costUnit || 'unit'}
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    selectedService.creditsPerAction && (
                      <div>
                        <label className="text-sm font-medium">Credits per Action</label>
                        <p className="text-sm text-muted-foreground">
                          {selectedService.creditsPerAction} credits
                        </p>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* Capabilities */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Capabilities & Limits
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  {selectedService.maxTokens && (
                    <div>
                      <label className="text-sm font-medium">Max Tokens</label>
                      <p className="text-sm text-muted-foreground">
                        {selectedService.maxTokens.toLocaleString()}
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium">Streaming Support</label>
                    <p className="text-sm text-muted-foreground">
                      {selectedService.supportsStreaming ? 'Yes' : 'No'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Function Calling</label>
                    <p className="text-sm text-muted-foreground">
                      {selectedService.supportsFunctionCalling ? 'Yes' : 'No'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Vision Support</label>
                    <p className="text-sm text-muted-foreground">
                      {selectedService.supportsVision ? 'Yes' : 'No'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Status & Metadata
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Status</label>
                    <p className="text-sm text-muted-foreground">
                      {selectedService.isActive ? 'Active' : 'Inactive'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Action Qualifier</label>
                    <p className="text-sm text-muted-foreground">
                      {selectedService.actionQualifier}
                    </p>
                  </div>
                  {selectedService.deprecationDate && (
                    <div>
                      <label className="text-sm font-medium">Deprecation Date</label>
                      <p className="text-sm text-muted-foreground">
                        {new Date(selectedService.deprecationDate).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium">Created</label>
                    <p className="text-sm text-muted-foreground">
                      {new Date(selectedService.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Service Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Service Definition</DialogTitle>
            <DialogDescription>
              Create a new service definition for LLM models or other AI services
            </DialogDescription>
          </DialogHeader>
          
          {/* Error Message */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Basic Information
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="serviceIdentifier">Service Identifier *</Label>
                  <Input
                    id="serviceIdentifier"
                    value={newService.serviceIdentifier || ''}
                    onChange={(e) => setNewService({ ...newService, serviceIdentifier: e.target.value })}
                    placeholder="e.g., gpt-4o"
                  />
                </div>
                <div>
                  <Label htmlFor="displayName">Display Name *</Label>
                  <Input
                    id="displayName"
                    value={newService.displayName || ''}
                    onChange={(e) => setNewService({ ...newService, displayName: e.target.value })}
                    placeholder="e.g., GPT-4 Optimized"
                  />
                </div>
                <div>
                  <Label htmlFor="providerId">Provider</Label>
                  <Select
                    value={newService.providerId || ''}
                    onValueChange={(value) => setNewService({ ...newService, providerId: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.map(provider => (
                        <SelectItem key={provider.id} value={provider.id}>
                          {provider.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="serviceType">Service Type *</Label>
                  <Select
                    value={newService.serviceType || 'llm'}
                    onValueChange={(value) => setNewService({ ...newService, serviceType: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="llm">LLM</SelectItem>
                      <SelectItem value="image_generation">Image Generation</SelectItem>
                      <SelectItem value="embedding">Embedding</SelectItem>
                      <SelectItem value="search">Search</SelectItem>
                      <SelectItem value="storage">Storage</SelectItem>
                      <SelectItem value="knowledge_base">Knowledge Base</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="intelligenceTier">Intelligence Tier</Label>
                  <Select
                    value={newService.intelligenceTier || 'none'}
                    onValueChange={(value) => setNewService({ ...newService, intelligenceTier: value === 'none' ? null : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select tier (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="actionQualifier">Action Qualifier *</Label>
                  <Select
                    value={newService.actionQualifier || 'single'}
                    onValueChange={(value) => setNewService({ ...newService, actionQualifier: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="chat_and_reply">Chat and Reply</SelectItem>
                      <SelectItem value="chat_action">Chat Action</SelectItem>
                      <SelectItem value="streaming">Streaming</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="maxTokens">Max Tokens</Label>
                  <Input
                    id="maxTokens"
                    type="number"
                    value={newService.maxTokens || ''}
                    onChange={(e) => setNewService({ ...newService, maxTokens: parseInt(e.target.value) || null })}
                    placeholder="e.g., 4096"
                  />
                </div>
              </div>
            </div>

            {/* Pricing Configuration */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Pricing Configuration
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <div className="flex items-center space-x-2 mb-4">
                    <Checkbox
                      id="usageBased"
                      checked={newService.usageBased || false}
                      onCheckedChange={(checked) => setNewService({ ...newService, usageBased: !!checked })}
                    />
                    <Label htmlFor="usageBased">Usage-based pricing (calculate from actual usage)</Label>
                  </div>
                </div>
                
                {!newService.usageBased && (
                  <div>
                    <Label htmlFor="creditsPerAction">Credits per Action</Label>
                    <Input
                      id="creditsPerAction"
                      type="number"
                      step="0.01"
                      value={newService.creditsPerAction || ''}
                      onChange={(e) => setNewService({ ...newService, creditsPerAction: parseFloat(e.target.value) || null })}
                      placeholder="e.g., 1.5"
                    />
                  </div>
                )}
                
                {/* Always show cost fields for LLM models for internal cost tracking */}
                {newService.serviceType === 'llm' && (
                  <>
                    <div>
                      <Label htmlFor="inputCost">
                        Input Cost (USD per Million)
                        {!newService.usageBased && <span className="text-xs text-muted-foreground ml-1">(for tracking)</span>}
                      </Label>
                      <Input
                        id="inputCost"
                        type="number"
                        step="0.01"
                        value={newService.inputCostUsdPerMillion || ''}
                        onChange={(e) => setNewService({ ...newService, inputCostUsdPerMillion: parseFloat(e.target.value) || null })}
                        placeholder="e.g., 5.00"
                      />
                    </div>
                    <div>
                      <Label htmlFor="outputCost">
                        Output Cost (USD per Million)
                        {!newService.usageBased && <span className="text-xs text-muted-foreground ml-1">(for tracking)</span>}
                      </Label>
                      <Input
                        id="outputCost"
                        type="number"
                        step="0.01"
                        value={newService.outputCostUsdPerMillion || ''}
                        onChange={(e) => setNewService({ ...newService, outputCostUsdPerMillion: parseFloat(e.target.value) || null })}
                        placeholder="e.g., 15.00"
                      />
                    </div>
                  </>
                )}
                
                {newService.serviceType !== 'llm' && (
                  <>
                    <div>
                      <Label htmlFor="costUsdPerUnit">
                        Cost USD per Unit
                        {!newService.usageBased && <span className="text-xs text-muted-foreground ml-1">(for tracking)</span>}
                      </Label>
                      <Input
                        id="costUsdPerUnit"
                        type="number"
                        step="0.000001"
                        value={newService.costUsdPerUnit || ''}
                        onChange={(e) => setNewService({ ...newService, costUsdPerUnit: parseFloat(e.target.value) || null })}
                        placeholder="e.g., 0.02"
                      />
                    </div>
                    <div>
                      <Label htmlFor="costUnit">Cost Unit</Label>
                      <Input
                        id="costUnit"
                        value={newService.costUnit || ''}
                        onChange={(e) => setNewService({ ...newService, costUnit: e.target.value || null })}
                        placeholder="e.g., gb_per_month, per_query"
                      />
                    </div>
                  </>
                )}
                
                <div className="col-span-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="isRecurring"
                      checked={newService.isRecurring || false}
                      onCheckedChange={(checked) => setNewService({ ...newService, isRecurring: !!checked })}
                    />
                    <Label htmlFor="isRecurring">Recurring charge</Label>
                  </div>
                </div>
                
                {newService.isRecurring && (
                  <div>
                    <Label htmlFor="billingPeriod">Billing Period</Label>
                    <Select
                      value={newService.billingPeriod || ''}
                      onValueChange={(value) => setNewService({ ...newService, billingPeriod: value || null })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select period" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div className="col-span-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="billedToUser"
                      checked={newService.billedToUser !== false}
                      onCheckedChange={(checked) => setNewService({ ...newService, billedToUser: !!checked })}
                    />
                    <Label htmlFor="billedToUser">Billed to user (deduct from credits)</Label>
                  </div>
                </div>
              </div>
            </div>

            {/* Capabilities */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Capabilities & Status
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="supportsStreaming"
                      checked={newService.supportsStreaming || false}
                      onCheckedChange={(checked) => setNewService({ ...newService, supportsStreaming: !!checked })}
                    />
                    <Label htmlFor="supportsStreaming">Supports Streaming</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="supportsFunctionCalling"
                      checked={newService.supportsFunctionCalling || false}
                      onCheckedChange={(checked) => setNewService({ ...newService, supportsFunctionCalling: !!checked })}
                    />
                    <Label htmlFor="supportsFunctionCalling">Supports Function Calling</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="supportsVision"
                      checked={newService.supportsVision || false}
                      onCheckedChange={(checked) => setNewService({ ...newService, supportsVision: !!checked })}
                    />
                    <Label htmlFor="supportsVision">Supports Vision</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="isActive"
                      checked={newService.isActive !== false}
                      onCheckedChange={(checked) => setNewService({ ...newService, isActive: !!checked })}
                    />
                    <Label htmlFor="isActive">Active</Label>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="deprecationDate">Deprecation Date</Label>
                  <Input
                    id="deprecationDate"
                    type="date"
                    value={newService.deprecationDate ? new Date(newService.deprecationDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => setNewService({ ...newService, deprecationDate: e.target.value || null })}
                  />
                </div>
                
                <div>
                  <Label htmlFor="replacementServiceId">Replacement Service ID</Label>
                  <Input
                    id="replacementServiceId"
                    value={newService.replacementServiceId || ''}
                    onChange={(e) => setNewService({ ...newService, replacementServiceId: e.target.value || null })}
                    placeholder="UUID of replacement service"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="metadata">Metadata (JSON)</Label>
                  <Input
                    id="metadata"
                    value={newService.metadata ? JSON.stringify(newService.metadata) : ''}
                    onChange={(e) => {
                      try {
                        const parsed = e.target.value ? JSON.parse(e.target.value) : null;
                        setNewService({ ...newService, metadata: parsed });
                      } catch {
                        // Invalid JSON, just store as string temporarily
                      }
                    }}
                    placeholder='{"key": "value"}'
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddService} disabled={saving}>
                {saving ? 'Creating...' : 'Create Service'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Service Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Service Definition</DialogTitle>
            <DialogDescription>
              Update service configuration and pricing details
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Basic Information
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-serviceIdentifier">Service Identifier *</Label>
                  <Input
                    id="edit-serviceIdentifier"
                    value={editDialogService.serviceIdentifier || ''}
                    onChange={(e) => setEditDialogService({ ...editDialogService, serviceIdentifier: e.target.value })}
                    placeholder="e.g., gpt-4o"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-displayName">Display Name *</Label>
                  <Input
                    id="edit-displayName"
                    value={editDialogService.displayName || ''}
                    onChange={(e) => setEditDialogService({ ...editDialogService, displayName: e.target.value })}
                    placeholder="e.g., GPT-4 Optimized"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-providerId">Provider</Label>
                  <Select
                    value={editDialogService.providerId || ''}
                    onValueChange={(value) => setEditDialogService({ ...editDialogService, providerId: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.map(provider => (
                        <SelectItem key={provider.id} value={provider.id}>
                          {provider.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="edit-serviceType">Service Type *</Label>
                  <Select
                    value={editDialogService.serviceType || ''}
                    onValueChange={(value) => setEditDialogService({ ...editDialogService, serviceType: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="llm">LLM</SelectItem>
                      <SelectItem value="image_generation">Image Generation</SelectItem>
                      <SelectItem value="embedding">Embedding</SelectItem>
                      <SelectItem value="search">Search</SelectItem>
                      <SelectItem value="storage">Storage</SelectItem>
                      <SelectItem value="knowledge_base">Knowledge Base</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="edit-intelligenceTier">Intelligence Tier</Label>
                  <Select
                    value={editDialogService.intelligenceTier || 'none'}
                    onValueChange={(value) => setEditDialogService({ ...editDialogService, intelligenceTier: value === 'none' ? null : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select tier (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="edit-actionQualifier">Action Qualifier *</Label>
                  <Select
                    value={editDialogService.actionQualifier || 'single'}
                    onValueChange={(value) => setEditDialogService({ ...editDialogService, actionQualifier: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="chat_and_reply">Chat and Reply</SelectItem>
                      <SelectItem value="chat_action">Chat Action</SelectItem>
                      <SelectItem value="streaming">Streaming</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="edit-maxTokens">Max Tokens</Label>
                  <Input
                    id="edit-maxTokens"
                    type="number"
                    value={editDialogService.maxTokens || ''}
                    onChange={(e) => setEditDialogService({ ...editDialogService, maxTokens: parseInt(e.target.value) || null })}
                    placeholder="e.g., 4096"
                  />
                </div>
              </div>
            </div>

            {/* Pricing Configuration */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Pricing Configuration
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <div className="flex items-center space-x-2 mb-4">
                    <Checkbox
                      id="edit-usageBased"
                      checked={editDialogService.usageBased || false}
                      onCheckedChange={(checked) => setEditDialogService({ ...editDialogService, usageBased: !!checked })}
                    />
                    <Label htmlFor="edit-usageBased">Usage-based pricing (calculate from actual usage)</Label>
                  </div>
                </div>
                
                {!editDialogService.usageBased && (
                  <div>
                    <Label htmlFor="edit-creditsPerAction">Credits per Action</Label>
                    <Input
                      id="edit-creditsPerAction"
                      type="number"
                      step="0.01"
                      value={editDialogService.creditsPerAction || ''}
                      onChange={(e) => setEditDialogService({ ...editDialogService, creditsPerAction: parseFloat(e.target.value) || null })}
                      placeholder="e.g., 1.5"
                    />
                  </div>
                )}
                
                {/* Always show cost fields for LLM models for internal cost tracking */}
                {editDialogService.serviceType === 'llm' && (
                  <>
                    <div>
                      <Label htmlFor="edit-inputCost">
                        Input Cost (USD per Million)
                        {!editDialogService.usageBased && <span className="text-xs text-muted-foreground ml-1">(for tracking)</span>}
                      </Label>
                      <Input
                        id="edit-inputCost"
                        type="number"
                        step="0.01"
                        value={editDialogService.inputCostUsdPerMillion || ''}
                        onChange={(e) => setEditDialogService({ ...editDialogService, inputCostUsdPerMillion: parseFloat(e.target.value) || null })}
                        placeholder="e.g., 5.00"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-outputCost">
                        Output Cost (USD per Million)
                        {!editDialogService.usageBased && <span className="text-xs text-muted-foreground ml-1">(for tracking)</span>}
                      </Label>
                      <Input
                        id="edit-outputCost"
                        type="number"
                        step="0.01"
                        value={editDialogService.outputCostUsdPerMillion || ''}
                        onChange={(e) => setEditDialogService({ ...editDialogService, outputCostUsdPerMillion: parseFloat(e.target.value) || null })}
                        placeholder="e.g., 15.00"
                      />
                    </div>
                  </>
                )}
                
                {editDialogService.serviceType !== 'llm' && (
                  <>
                    <div>
                      <Label htmlFor="edit-costUsdPerUnit">
                        Cost USD per Unit
                        {!editDialogService.usageBased && <span className="text-xs text-muted-foreground ml-1">(for tracking)</span>}
                      </Label>
                      <Input
                        id="edit-costUsdPerUnit"
                        type="number"
                        step="0.000001"
                        value={editDialogService.costUsdPerUnit || ''}
                        onChange={(e) => setEditDialogService({ ...editDialogService, costUsdPerUnit: parseFloat(e.target.value) || null })}
                        placeholder="e.g., 0.02"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-costUnit">Cost Unit</Label>
                      <Input
                        id="edit-costUnit"
                        value={editDialogService.costUnit || ''}
                        onChange={(e) => setEditDialogService({ ...editDialogService, costUnit: e.target.value || null })}
                        placeholder="e.g., gb_per_month, per_query"
                      />
                    </div>
                  </>
                )}
                
                <div className="col-span-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-isRecurring"
                      checked={editDialogService.isRecurring || false}
                      onCheckedChange={(checked) => setEditDialogService({ ...editDialogService, isRecurring: !!checked })}
                    />
                    <Label htmlFor="edit-isRecurring">Recurring charge</Label>
                  </div>
                </div>
                
                {editDialogService.isRecurring && (
                  <div>
                    <Label htmlFor="edit-billingPeriod">Billing Period</Label>
                    <Select
                      value={editDialogService.billingPeriod || ''}
                      onValueChange={(value) => setEditDialogService({ ...editDialogService, billingPeriod: value || null })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select period" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div className="col-span-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-billedToUser"
                      checked={editDialogService.billedToUser !== false}
                      onCheckedChange={(checked) => setEditDialogService({ ...editDialogService, billedToUser: !!checked })}
                    />
                    <Label htmlFor="edit-billedToUser">Billed to user (deduct from credits)</Label>
                  </div>
                </div>
              </div>
            </div>

            {/* Capabilities & Status */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Capabilities & Status
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-supportsStreaming"
                      checked={editDialogService.supportsStreaming || false}
                      onCheckedChange={(checked) => setEditDialogService({ ...editDialogService, supportsStreaming: !!checked })}
                    />
                    <Label htmlFor="edit-supportsStreaming">Supports Streaming</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-supportsFunctionCalling"
                      checked={editDialogService.supportsFunctionCalling || false}
                      onCheckedChange={(checked) => setEditDialogService({ ...editDialogService, supportsFunctionCalling: !!checked })}
                    />
                    <Label htmlFor="edit-supportsFunctionCalling">Supports Function Calling</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-supportsVision"
                      checked={editDialogService.supportsVision || false}
                      onCheckedChange={(checked) => setEditDialogService({ ...editDialogService, supportsVision: !!checked })}
                    />
                    <Label htmlFor="edit-supportsVision">Supports Vision</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-isActive"
                      checked={editDialogService.isActive !== false}
                      onCheckedChange={(checked) => setEditDialogService({ ...editDialogService, isActive: !!checked })}
                    />
                    <Label htmlFor="edit-isActive">Active</Label>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="edit-deprecationDate">Deprecation Date</Label>
                  <Input
                    id="edit-deprecationDate"
                    type="date"
                    value={editDialogService.deprecationDate ? new Date(editDialogService.deprecationDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => setEditDialogService({ ...editDialogService, deprecationDate: e.target.value || null })}
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit-replacementServiceId">Replacement Service ID</Label>
                  <Input
                    id="edit-replacementServiceId"
                    value={editDialogService.replacementServiceId || ''}
                    onChange={(e) => setEditDialogService({ ...editDialogService, replacementServiceId: e.target.value || null })}
                    placeholder="UUID of replacement service"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="edit-metadata">Metadata (JSON)</Label>
                  <Input
                    id="edit-metadata"
                    value={editDialogService.metadata ? JSON.stringify(editDialogService.metadata) : ''}
                    onChange={(e) => {
                      try {
                        const parsed = e.target.value ? JSON.parse(e.target.value) : null;
                        setEditDialogService({ ...editDialogService, metadata: parsed });
                      } catch {
                        // Invalid JSON, just store as string temporarily
                      }
                    }}
                    placeholder='{"key": "value"}'
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleEditCancel}>
                Cancel
              </Button>
              <Button onClick={handleEditSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceDefinitionsPage;